/* eslint-disable no-multiple-empty-lines */
import { html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { until } from 'lit/directives/until.js';
import { api, log, _converse, u, constants } from '@converse/headless';
import { CustomElement } from 'shared/components/element.js';
import ForwardModal from 'shared/modals/forward-modal.js';
import { __ } from 'i18n';
import { isMediaURLDomainAllowed, isDomainWhitelisted } from 'utils/url.js';
import { isSelectingMessages, startSelecting, toggleMessageSelection } from 'shared/chat/selection.js';

import './styles/message-actions.scss';
import './reaction-panel.js';

const { getMediaURLs } = u;
const { CHATROOMS_TYPE } = constants;

/**
 * @typedef {Object} MessageActionAttributes
 * An object which represents a message action (as shown in the message dropdown);
 * @property {String} i18n_text
 * @property {Function} handler
 * @property {String} button_class
 * @property {String} icon_class
 * @property {String} name
 */

class MessageActions extends CustomElement {
    /**
     * @typedef {import('@converse/headless/types/utils/types').MediaURLMetadata} MediaURLMetadata
     */

    static get properties() {
        return {
            is_retracted: { type: Boolean },
            model: { type: Object },
        };
    }

    constructor() {
        super();
        this.model = null;
        this.is_retracted = null;
    }

    initialize() {
        const settings = api.settings.get();
        this.listenTo(settings, 'change:allowed_audio_domains', () => this.requestUpdate());
        this.listenTo(settings, 'change:allowed_image_domains', () => this.requestUpdate());
        this.listenTo(settings, 'change:allowed_video_domains', () => this.requestUpdate());
        this.listenTo(settings, 'change:render_media', () => this.requestUpdate());
        this.listenTo(this.model, 'change', () => this.requestUpdate());

        // This may change the ability to send messages, and therefore the presence of the quote button.
        // See plugins/muc-views/bottom-panel.js
        this.listenTo(this.model.chatbox.features, 'change:moderated', () => this.requestUpdate());
        this.listenTo(this.model.chatbox.occupants, 'add', this.updateIfOwnOccupant);
        this.listenTo(this.model.chatbox.occupants, 'change:role', this.updateIfOwnOccupant);
        this.listenTo(this.model.chatbox.session, 'change:connection_status', () => this.requestUpdate());
    }

    updateIfOwnOccupant(o) {
        const bare_jid = _converse.session.get('bare_jid');
        if (o.get('jid') === bare_jid) {
            this.requestUpdate();
        }
    }

    render() {
        return html`${until(this.renderActions(), '')}`;
    }

    async renderActions() {
        if (!this.model.collection) return '';

        const buttons = await this.getActionButtons();
        // ПЕРЕДАЕМ this.model ВТОРЫМ АРГУМЕНТОМ
        const items = buttons.map((b) => MessageActions.getActionsDropdownItem(b, this.model));

        if (items.length) {
            return html`<converse-dropdown class="chat-msg__actions btn-group dropstart" .items="${items}"></converse-dropdown>`;
        } else {
            return '';
        }
    }

    // async renderActions() {
    //     // This can be called before the model has been added to the collection
    //     // when requesting an update on change:connection_status.
    //     // This line allows us to pass tests.
    //     if (!this.model.collection) return '';

    //     const buttons = await this.getActionButtons();
    //     const items = buttons.map((b) => MessageActions.getActionsDropdownItem(b));
    //     if (items.length) {
    //         return html`<converse-dropdown
    //             class="chat-msg__actions btn-group dropstart"
    //             .items=${items}
    //         ></converse-dropdown>`;
    //     } else {
    //         return '';
    //     }
    // }

    static getActionsDropdownItem(o, model) {
        if (o.name === 'react') {
            return html`<button type="button" class="dropdown-item chat-msg__action chat-msg__action-react" @click="${(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const btn = /** @type {HTMLElement} */ (e.currentTarget);
                        const btnRect = btn.getBoundingClientRect();
                        const isMe = !!btn.closest('.chat-msg__content--me');
                        const rect = btnRect;
                        if (!rect) return;
                        document.querySelector('converse-reaction-panel')?.remove();
                        const panel = /** @type {any} */ (document.createElement('converse-reaction-panel'));
                        panel.model = model;
                        document.body.appendChild(panel);
                        panel.updateComplete.then(() =>
                            requestAnimationFrame(() => panel.positionNear(rect, isMe))
                        );
                    }}"><converse-icon class="far fa-smile" size="1.2em"></converse-icon>${o.i18n_text}</button>`;
        }

        const iconColor = o.is_danger ? '#e05252' : '#8099c0';
        const iconEl = o.svg_icon
            ? html`<span class="chat-msg__action-svg-icon">${unsafeHTML(o.svg_icon)}</span>`
            : html`<converse-icon class="${o.icon_class}" color="${iconColor}" size="1.2em"></converse-icon>`;
        return html`<button type="button" class="dropdown-item chat-msg__action ${o.button_class} ${o.is_danger ? 'chat-msg__action--danger' : ''}" @click="${o.handler}">${iconEl} ${o.i18n_text}</button>`;
    }

    // static getActionsDropdownItem(o) {
    //     return html`
    //         <button type="button" class="dropdown-item chat-msg__action ${o.button_class}" @click=${o.handler}>
    //             <converse-icon class="${o.icon_class}" color="var(--foreground-color)" size="1em"></converse-icon
    //             >&nbsp;${o.i18n_text}
    //         </button>
    //     `;
    // }

    /** * Обработчик клика по кнопке реакции.
     * @param {MouseEvent} ev
     */
    // async onMessageReactionButtonClicked(ev) {
    //     ev.preventDefault();
    //     ev.stopPropagation();

    //     // Кастуем target к HTMLElement для корректной работы getBoundingClientRect в TS
    //     const target = /** @type {HTMLElement} */ (ev.target);
    //     const rect = target.getBoundingClientRect();

    //     api.trigger('toggleEmojiPicker', {
    //         'model': this.model,
    //         'source': target,
    //         'type': 'reaction',
    //         'position': {
    //             'top': rect.top,
    //             'left': rect.left,
    //         },
    //     });
    // }

    /** @param {MouseEvent} ev */
    async onMessageEditButtonClicked(ev) {
        ev.preventDefault();
        const currently_correcting = this.model.collection.findWhere('correcting');
        // TODO: Use state instead of DOM querying
        // Then this code can also be put on the model
        const unsent_text = u.ancestor(this, '.chatbox')?.querySelector('.chat-textarea')?.value;
        if (unsent_text && (!currently_correcting || currently_correcting.getMessageText() !== unsent_text)) {
            const result = await api.confirm(
                __('Confirm'),
                __('You have an unsent message which will be lost if you continue. Are you sure?'),
            );
            if (!result) return;
        }
        if (currently_correcting !== this.model) {
            currently_correcting?.save('correcting', false);
            this.model.save('correcting', true);
        } else {
            this.model.save('correcting', false);
        }
    }

    async onDirectMessageRetractButtonClicked() {
        if (this.model.get('sender') !== 'me') {
            return log.error("onMessageRetractButtonClicked called for someone else's message!");
        }
        const retraction_warning = __(
            'Be aware that other XMPP/Jabber clients (and servers) may ' +
                'not yet support retractions and that this message may not ' +
                'be removed everywhere.',
        );
        const messages = [__('Are you sure you want to retract this message?')];
        if (api.settings.get('show_retraction_warning')) {
            messages[1] = retraction_warning;
        }
        const result = await api.confirm(__('Confirm'), messages);
        if (result) {
            const chatbox = this.model.collection.chatbox;
            chatbox.retractOwnMessage(this.model);
        }
    }

    /**
     * Retract someone else's message in this groupchat.
     * @param {string} [reason] - The reason for retracting the message.
     */
    async retractOtherMessage(reason) {
        const chatbox = this.model.collection.chatbox;
        const result = await chatbox.retractOtherMessage(this.model, reason);
        if (result === null) {
            const err_msg = __(`A timeout occurred while trying to retract the message`);
            api.alert('error', __('Error'), err_msg);
            log.warn(err_msg);
        } else if (u.isErrorStanza(result)) {
            const err_msg = __(`Sorry, you're not allowed to retract this message.`);
            api.alert('error', __('Error'), err_msg);
            log.warn(err_msg);
            log.error(result);
        }
    }

    async onMUCMessageRetractButtonClicked() {
        const retraction_warning = __(
            'Be aware that other XMPP/Jabber clients (and servers) may ' +
                'not yet support retractions and that this message may not ' +
                'be removed everywhere.',
        );

        if (this.model.mayBeRetracted()) {
            const messages = [__('Are you sure you want to retract this message?')];
            if (api.settings.get('show_retraction_warning')) {
                messages[1] = retraction_warning;
            }
            if (await api.confirm(__('Confirm'), messages)) {
                const chatbox = this.model.collection.chatbox;
                chatbox.retractOwnMessage(this.model);
            }
        } else if (await this.model.mayBeModerated()) {
            if (this.model.get('sender') === 'me') {
                let messages = [__('Are you sure you want to retract this message?')];
                if (api.settings.get('show_retraction_warning')) {
                    messages = [messages[0], retraction_warning, messages[1]];
                }
                !!(await api.confirm(__('Confirm'), messages)) && this.retractOtherMessage();
            } else {
                let messages = [
                    __('You are about to retract this message.'),
                    __('You may optionally include a message, explaining the reason for the retraction.'),
                ];
                if (api.settings.get('show_retraction_warning')) {
                    messages = [messages[0], retraction_warning, messages[1]];
                }
                const reason = await api.prompt(__('Message Retraction'), messages, __('Optional reason'));
                reason !== false && this.retractOtherMessage(reason);
            }
        } else {
            const err_msg = __(`Sorry, you're not allowed to retract this message`);
            api.alert('error', __('Error'), err_msg);
        }
    }

    /** @param {MouseEvent} [ev] */
    onMessageRetractButtonClicked(ev) {
        ev?.preventDefault?.();
        const chatbox = this.model.collection.chatbox;
        if (chatbox.get('type') === CHATROOMS_TYPE) {
            this.onMUCMessageRetractButtonClicked();
        } else {
            this.onDirectMessageRetractButtonClicked();
        }
    }

    /** @param {MouseEvent} [ev] */
    onMediaToggleClicked(ev) {
        ev?.preventDefault?.();

        if (this.hasHiddenMedia(this.getMediaURLs())) {
            this.model.save({
                'hide_url_previews': false,
                'url_preview_transition': 'fade-in',
            });
        } else {
            const ogp_metadata = this.model.get('ogp_metadata') || [];
            if (ogp_metadata.length) {
                this.model.set('url_preview_transition', 'fade-out');
            } else {
                this.model.save({
                    'hide_url_previews': true,
                    'url_preview_transition': 'fade-in',
                });
            }
        }
    }

    /**
     * Check whether media is hidden or shown, which is used to determine the toggle text.
     *
     * If `render_media` is an array, check if there are media URLs outside
     * of that array, in which case we consider message media on the whole to be hidden (since
     * those excluded by the whitelist will be, even if the render_media whitelisted URLs are shown).
     * @param { Array<String> } media_urls
     * @returns { Boolean }
     */
    hasHiddenMedia(media_urls) {
        if (typeof this.model.get('hide_url_previews') === 'boolean') {
            return this.model.get('hide_url_previews');
        }
        const render_media = api.settings.get('render_media');
        if (Array.isArray(render_media)) {
            return media_urls.reduce((acc, url) => acc || !isDomainWhitelisted(render_media, url), false);
        } else {
            return !render_media;
        }
    }

    getMediaURLs() {
        const unfurls_to_show = (this.model.get('ogp_metadata') || [])
            .map((o) => ({ 'url': o['og:image'], 'is_image': true }))
            .filter((o) => isMediaURLDomainAllowed(o));

        const url_strings = getMediaURLs(this.model.get('media_urls') || [], this.model.get('body'));
        const media_urls = /** @type {MediaURLMetadata[]} */ (url_strings.filter((o) => isMediaURLDomainAllowed(o)));
        return [...new Set([...media_urls.map((o) => o.url), ...unfurls_to_show.map((o) => o.url)])];
    }

    /**
     * Adds a media rendering toggle to this message's action buttons if necessary.
     *
     * The toggle is only added if the message contains media URLs and if the
     * user is allowed to show or hide media for those URLs.
     *
     * Whether a user is allowed to show or hide domains depends on the config settings:
     * * allowed_audio_domains
     * * allowed_video_domains
     * * allowed_image_domains
     *
     * Whether media is currently shown or hidden is determined by the { @link hasHiddenMedia } method.
     *
     * @param { Array<MessageActionAttributes> } buttons - An array of objects representing action buttons
     */
    addMediaRenderingToggle(buttons) {
        const urls = this.getMediaURLs();
        if (urls.length) {
            const hidden = this.hasHiddenMedia(urls);
            buttons.push({
                'i18n_text': hidden ? __('Show media') : __('Hide media'),
                'handler': (ev) => this.onMediaToggleClicked(ev),
                'button_class': 'chat-msg__action-hide-previews',
                'icon_class': hidden ? 'fas fa-eye' : 'fas fa-eye-slash',
                'name': 'hide',
            });
        }
    }

    /** @param {MouseEvent} [ev] */
    async onMessageCopyButtonClicked(ev) {
        ev?.preventDefault?.();
        await navigator.clipboard.writeText(this.model.getMessageText());
    }

    /**
     * Whether this message has an image worth offering a download button
     * for. Reuses getMediaURLs() (the same "does this message have visual
     * media" check the show/hide-media toggle already relies on) for plain
     * image URLs, plus a body-text check for OMEMO-encrypted attachments
     * (`aesgcm://...`), which getMediaURLs() doesn't match since that's not
     * a fetchable http(s) URL -- see plugins/omemo-views/utils.js for how
     * those actually get decrypted and rendered.
     * @returns {boolean}
     */
    hasDownloadableImage() {
        if (this.getMediaURLs().length) return true;
        const body = this.model.get('body') || '';
        return /aesgcm:\/\/\S+\.(jpe?g|png|gif|webp|bmp|svg)(\?\S*)?/i.test(body);
    }

    /**
     * The actual resolved `src` (a `blob:` object URL for an already-
     * decrypted OMEMO image, or the plain remote URL otherwise) only
     * exists on the rendered `<img>` itself -- same DOM lookup already
     * used by the image click-to-enlarge handler's `ev.target.src` (see
     * shared/chat/message-body.js#onImgClick). The message body/model only
     * ever has the pre-decryption `aesgcm://...` URL for an OMEMO image,
     * which isn't independently fetchable/downloadable.
     * @param {MouseEvent} [ev]
     */
    onMessageDownloadButtonClicked(ev) {
        ev?.preventDefault?.();
        const img = /** @type {HTMLImageElement} */ (
            u.ancestor(this, '.chat-msg__content')?.querySelector('.chat-image')
        );
        if (!img?.src) {
            api.alert('error', __('Помилка'), __('Не вдалося знайти зображення для завантаження'));
            return;
        }
        const link = document.createElement('a');
        link.href = img.src;
        link.download = this.getDownloadFilename(img.src);
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    /**
     * @param {string} src
     * @returns {string}
     */
    getDownloadFilename(src) {
        try {
            const { pathname } = new URL(src, window.location.href);
            const last = pathname.split('/').pop();
            // A blob: URL's "path" is just an opaque UUID with no extension
            // (the original filename never makes it onto the <img> element
            // itself, only into the short-lived File object at decrypt
            // time) -- fall back to a generic but still usable name instead.
            return last && last.includes('.') ? last : `image-${Date.now()}.jpg`;
        } catch {
            return `image-${Date.now()}.jpg`;
        }
    }

    /** @param {MouseEvent} [ev] */
    //     onMessageQuoteButtonClicked (ev) {
    //     ev?.preventDefault?.();
    //     const chatbox = this.model.collection.chatbox;

    //     // Вместо вставки текста в draft, сохраняем данные цитаты в специальный атрибут
    //     chatbox.save({
    //         'quoted_message_data': {
    //             'body': this.model.getMessageText(),
    //             'author': this.model.getDisplayName(),
    //             'msg_id': this.model.get('msgid')
    //         }
    //     });
    //     console.log('Saved quoted message data to chatbox model:', {
    //         'body': this.model.getMessageText(),
    //         'author': this.model.getDisplayName(),
    //         'msg_id': this.model.get('msgid')
    //     });

    //     // Фокусируемся на поле ввода (опционально)
    //     u.ancestor(this, '.chatbox')?.querySelector('.chat-textarea')?.focus();
    // }
    // onMessageQuoteButtonClicked (ev) {
    //     ev?.preventDefault?.();
    //     const chatbox = this.model.collection.chatbox;
    //     const idx = u.ancestor(this, '.chatbox')?.querySelector('.chat-textarea')?.selectionEnd;
    //     const new_text = this.model.getMessageText().replaceAll(/^/gm, '> ');
    //     let draft = chatbox.get('draft') ?? '';
    //     if (idx) {
    //         draft = `${draft.slice(0, idx)}\n${new_text}\n${draft.slice(idx)}`;
    //     } else {
    //         draft += new_text;
    //     }
    //     console.log('Saving quote to draft:', draft);
    //     chatbox.save({ draft });
    // }

    onMessageQuoteButtonClicked(ev) {
        ev?.preventDefault?.();
        const chatbox = this.model.collection.chatbox;

        // Сохраняем структурированные данные в модель чатбокса
        chatbox.save({
            'reply_to': {
                'body': this.model.getMessageText(),
                'author': this.model.getDisplayName(),
                'msgid': this.model.get('msgid'),
            },
        });

        // Фокусируемся на поле ввода
        u.ancestor(this, '.chatbox')?.querySelector('.chat-textarea')?.focus();
    }

    /** @param {MouseEvent} [ev] */
    onMessageSelectButtonClicked(ev) {
        ev?.preventDefault?.();
        const chatbox = this.model.collection.chatbox;
        const msgid = this.model.get('msgid');
        if (isSelectingMessages(chatbox)) {
            toggleMessageSelection(chatbox, msgid);
        } else {
            startSelecting(chatbox, msgid);
        }
    }

    // async onForwardButtonClicked(ev) {
    //     ev?.preventDefault?.();
    //     // Открываем модальное окно пересылки, передавая текущую модель сообщения
    //     const { ForwardModal } = await import('./forward-modal.js');
    //     new ForwardModal({ 'model': this.model }).show();
    // }

    async getActionButtons() {
        const buttons = [];

        // 1. Edit (own messages only)
        if (this.model.get('editable')) {
            buttons.push(/** @type {MessageActionAttributes} */ ({
                'i18n_text': this.model.get('correcting') ? __('Cancel Editing') : __('Редагувати'),
                'handler': (ev) => this.onMessageEditButtonClicked(ev),
                'button_class': 'chat-msg__action-edit',
                'icon_class': 'fas fa-pencil-alt',
                'name': 'edit',
            }));
        }

        // 2. Copy
        buttons.push({
            'i18n_text': __('Копіювати'),
            'handler': (ev) => this.onMessageCopyButtonClicked(ev),
            'button_class': 'chat-msg__action-copy',
            'icon_class': 'fas fa-copy',
            'name': 'copy',
        });

        // 3. Reply / Quote
        if (this.model.collection?.chatbox?.canPostMessages()) {
            buttons.push({
                'i18n_text': __('Відповісти'),
                'handler': (ev) => this.onMessageQuoteButtonClicked(ev),
                'button_class': 'chat-msg__action-quote',
                'icon_class': 'fas fa-reply',
                'svg_icon': `<svg width="20" height="15" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.3904 14.153V11.7337C18.3904 10.675 17.9905 9.66023 17.2792 8.91161C16.6124 8.20991 15.7235 7.79592 14.7862 7.74686L14.5976 7.74213H0.804804C0.360447 7.74213 0 7.36279 0 6.89515C0 6.4275 0.360447 6.04816 0.804804 6.04816H14.5976C16.0303 6.04816 17.4042 6.64758 18.4173 7.71378C19.4304 8.77999 20 10.2259 20 11.7337V14.153C20 14.6207 19.6396 15 19.1952 15C18.7508 15 18.3904 14.6207 18.3904 14.153Z" fill="currentColor"/><path d="M5.98271 0.248008C6.29692 -0.0826692 6.80668 -0.0826692 7.12089 0.248008C7.4351 0.578685 7.4351 1.11516 7.12089 1.44584L1.94298 6.89515L7.12089 12.3445C7.4351 12.6751 7.4351 13.2116 7.12089 13.5423C6.80668 13.873 6.29692 13.873 5.98271 13.5423L0.235717 7.49406C-0.0784912 7.16338 -0.0784912 6.62691 0.235717 6.29623L5.98271 0.248008Z" fill="currentColor"/></svg>`,
                'name': 'quote',
            });
        }

        // 4. Forward
        buttons.push({
            'i18n_text': __('Переслати'),
            'handler': (ev) => {
                ev?.preventDefault?.();
                api.modal.show('converse-forward-modal', {
                    model: this.model,
                    forwardText: this.model.getMessageText(),
                });
            },
            'button_class': 'chat-msg__action-forward',
            'icon_class': 'fas fa-paper-plane',
            'name': 'forward',
        });

        // 4b. Select (enters Telegram-style multi-select mode)
        // Note: the icon sprite (src/shared/components/templates/icons.js) has no
        // "check-square" symbol, only "check" — using anything else renders blank.
        buttons.push({
            'i18n_text': __('Виділити'),
            'handler': (ev) => this.onMessageSelectButtonClicked(ev),
            'button_class': 'chat-msg__action-select',
            'icon_class': 'fas fa-check',
            'name': 'select',
        });

        const may_be_moderated =
            ['groupchat', 'mep'].includes(this.model.get('type')) && (await this.model.mayBeModerated());
        const retractable = !this.is_retracted && (this.model.mayBeRetracted() || may_be_moderated);

        if (!this.model.collection) return [];

        this.addMediaRenderingToggle(buttons);

        // 4c. Download (only for messages with an image attachment)
        if (this.hasDownloadableImage()) {
            buttons.push({
                'i18n_text': __('Завантажити'),
                'handler': (ev) => this.onMessageDownloadButtonClicked(ev),
                'button_class': 'chat-msg__action-download',
                'icon_class': 'fa fa-download',
                'name': 'download',
            });
        }

        // 5. Delete / Retract (last, red)
        if (retractable) {
            buttons.push({
                'i18n_text': __('Видалити'),
                'handler': (ev) => this.onMessageRetractButtonClicked(ev),
                'button_class': 'chat-msg__action-retract',
                'icon_class': 'fas fa-trash-alt',
                'is_danger': true,
                'name': 'retract',
            });
        }

        return api.hook('getMessageActionButtons', this, buttons);
    }
}

api.elements.define('converse-message-actions', MessageActions);
